import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, AlertCircle, Mail, Download, Home } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TeamWERK Logo component for checkout pages
const TeamWerkLogo = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
        <span className="text-2xl font-bold tracking-tight">
            team<span className="text-black">WERK</span>
        </span>
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 21V9L12 3L21 9V21H15V14H9V21H3Z" fill="currentColor"/>
        </svg>
    </div>
);

export const TicketSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [order, setOrder] = useState(null);
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');  // kostenloser Pfad (register-free)

    useEffect(() => {
        if (sessionId) {
            checkPaymentStatus();
        } else if (orderId) {
            // Kostenlose Anmeldung: Order ist serverseitig bereits 'confirmed'
            // und die Bestätigungsmail wurde versandt — kein Polling nötig.
            setStatus('success');
        } else {
            setStatus('error');
        }
    }, [sessionId, orderId]);

    const checkPaymentStatus = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tickets/checkout/status/${sessionId}`);
            
            if (response.data.payment_status === 'paid') {
                setStatus('success');
                // Try to get order details (optional)
                // Note: We don't have a direct endpoint to get order by session_id from frontend
            } else {
                // Check again in 2 seconds
                setTimeout(checkPaymentStatus, 2000);
            }
        } catch (error) {
            console.error('Error checking status:', error);
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardContent className="p-8 text-center">
                    <TeamWerkLogo />
                    {status === 'loading' && (
                        <>
                            <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin text-gray-400" />
                            <h1 className="text-2xl font-bold mb-2">Zahlung wird verarbeitet...</h1>
                            <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="h-10 w-10 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">Buchung erfolgreich!</h1>
                            <p className="text-gray-600 mb-6">
                                Vielen Dank für Ihre Bestellung. Sie erhalten in Kürze eine Bestätigungs-E-Mail mit Ihren Tickets.
                            </p>
                            
                            <div className="bg-gray-100 p-4 rounded-lg mb-6 text-left">
                                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                    <Mail className="h-4 w-4" />
                                    <span>Bestätigung per E-Mail</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Prüfen Sie auch Ihren Spam-Ordner, falls Sie die E-Mail nicht finden.
                                </p>
                            </div>

                            <Button onClick={() => navigate('/')} className="w-full bg-black hover:bg-gray-800">
                                <Home className="h-4 w-4 mr-2" />
                                Zurück zur Startseite
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">Etwas ist schiefgelaufen</h1>
                            <p className="text-gray-600 mb-6">
                                Die Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.
                            </p>
                            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                                Zurück zur Startseite
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export const TicketCancel = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderId = searchParams.get('order_id');
    const [eventId, setEventId] = useState(null);

    useEffect(() => {
        // Cancel the pending order to release tickets
        if (orderId) {
            axios.post(`${API_URL}/api/tickets/cancel-pending/${orderId}`).catch(console.error);
            // Event-ID der Order holen, damit "Zurück zum Ticket-Shop" auf den richtigen Shop
            // verweist (NICHT navigate(-1) -> das landete wieder im Stripe-Zahlfenster).
            axios.get(`${API_URL}/api/tickets/orders/${orderId}`)
                .then((res) => setEventId(res.data?.event_id || null))
                .catch(console.error);
        }
    }, [orderId]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardContent className="p-8 text-center">
                    <TeamWerkLogo />
                    <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-10 w-10 text-yellow-600" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Buchung abgebrochen</h1>
                    <p className="text-gray-600 mb-6">
                        Die Buchung wurde abgebrochen. Es wurden keine Kosten berechnet.
                    </p>
                    <div className="space-y-2">
                        <Button onClick={() => navigate(eventId ? `/tickets/${eventId}` : '/')} className="w-full bg-black hover:bg-gray-800">
                            Zurück zum Ticket-Shop
                        </Button>
                        <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                            Zur Startseite
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default TicketSuccess;
